/**
 * Estilos compartidos para react-select
 * Uso: import { selectStyles, selectStylesSmall } from '../../Utils/selectStyles';
 */

// Colores del tema
const colors = {
    primary: '#11cdef',
    primaryLight: '#e8fafd',
    text: '#0033ff',
    textMuted: '#525f7f',
    white: '#fff',
    black: '#000',
};

/**
 * Estilos estándar para react-select
 * Usado en formularios con altura normal (44px)
 */
export const selectStyles = {
    control: (base, state) => ({
        ...base,
        backgroundColor: colors.white,
        borderBottom: `1px solid ${colors.primary}`,
        borderRadius: 6,
        boxShadow: `0 1px 0 0 ${colors.primary}`,
        minHeight: '2em',
        fontSize: 13,
        color: colors.black,
        marginLeft: '-2px',
    }),
    indicatorSeparator: (base) => ({
        ...base,
        height: '28px',
        marginTop: '8px',
        marginBottom: '8px'
    }),
    valueContainer: (base) => ({
        ...base,
        paddingTop: 0,
        height: 44,
    }), 
    option: (base, state) => ({
        ...base,
        fontSize: 13,
        color: state.isSelected ? colors.white : colors.text,
        backgroundColor: state.isSelected ? colors.primary : colors.white,
        ':active': { backgroundColor: colors.primaryLight },
        ':hover': { backgroundColor: state.isSelected ? colors.primary : colors.primaryLight },
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: colors.primaryLight,
        color: colors.text,
        borderRadius: 4,
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: colors.text,
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: colors.text,
        ':hover': {
            backgroundColor: colors.primary,
            color: colors.white,
        },
    }),
    placeholder: (base) => ({
        ...base,
        color: colors.textMuted,
        fontSize: 13,
    }),
    singleValue: (base) => ({
        ...base,
        color: colors.text,
        fontSize: 13,
    }),
    menu: (base) => ({
        ...base,
        zIndex: 9999,
    }),
};

/**
 * Estilos compactos para react-select
 * Usado en filtros, tablas o espacios reducidos
 */
export const selectStylesSmall = {
    ...selectStyles,
    control: (base, state) => ({
        ...selectStyles.control(base, state),
        minHeight: '32px',
        fontSize: 12,
    }),
    valueContainer: (base) => ({
        ...base,
        paddingTop: 0,
        height: 32,
    }),
    indicatorSeparator: (base) => ({
        ...base,
        height: '20px',
        marginTop: '6px',
        marginBottom: '6px'
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: 32,
    }),
    option: (base, state) => ({
        ...selectStyles.option(base, state),
        fontSize: 12,
        padding: '6px 12px',
    }),
    placeholder: (base) => ({
        ...base,
        color: colors.textMuted,
        fontSize: 12,
    }),
    singleValue: (base) => ({
        ...base,
        color: colors.text,
        fontSize: 12,
    }),
};

/**
 * Estilos para select deshabilitado
 */
export const selectStylesDisabled = {
    ...selectStyles,
    control: (base, state) => ({
        ...selectStyles.control(base, state),
        backgroundColor: '#f3f4f6',
        cursor: 'not-allowed',
    }),
};

/**
 * Genera estilos personalizados basados en los estilos base
 * @param {Object} overrides - Objeto con las propiedades a sobrescribir
 * @returns {Object} - Estilos combinados
 */
export const createSelectStyles = (overrides = {}) => {
    return Object.keys(selectStyles).reduce((acc, key) => {
        if (overrides[key]) {
            acc[key] = (base, state) => ({
                ...(typeof selectStyles[key] === 'function' 
                    ? selectStyles[key](base, state) 
                    : selectStyles[key]),
                ...(typeof overrides[key] === 'function' 
                    ? overrides[key](base, state) 
                    : overrides[key]),
            });
        } else {
            acc[key] = selectStyles[key];
        }
        return acc;
    }, {});
};

export default selectStyles;
